import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Page from "../../../components/Page";
import { auctionsLive } from "../../Model/DeclaratorModel";
import View from "./View";
import { makeStyles } from "@mui/styles";
import Container from "@mui/material/Container";
import { getUser } from "../../Model/LocalStorage";
import "./Center.css";
import { hostname } from "../../../config";
import useMediaQuery from "@mui/material/useMediaQuery";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: "white",
    minHeight: "100vh",
    paddingBottom: 3,
    paddingTop: 3,
  },
}));

const SettingsPlayers = () => {
  const classes = useStyles();
  const phoneMatch = useMediaQuery("(min-width:900px)");

  // --------------------------------------------------
  // STATES
  // --------------------------------------------------

  const [liveData, setLiveData] = useState(null);
  const [socket, setSocket] = useState(null);

  const [networkStatus, setNetworkStatus] = useState({
    isOnline: true,
    message: "",
  });

  // Prevent socket / useEffect loops
  const socketRef = useRef(null);

  // --------------------------------------------------
  // FETCH AUCTION DATA
  // --------------------------------------------------

  const fetchAuctionData = async (socketInstance) => {
    try {
      const results = await auctionsLive();

      if (!results || results.success === false) {
        setNetworkStatus({
          isOnline: false,
          message:
            results?.msg || "Failed to load",
        });

        return;
      }

      setNetworkStatus({
        isOnline: true,
        message: "",
      });

      setLiveData(results);

      // Join auction room only when socket exists
      if (socketInstance) {
        socketInstance.emit(
          "joint_room",
          results.auction_details.auction_id
        );
      }
    } catch (error) {
      console.error(
        "ListData Error:",
        error
      );

      setNetworkStatus({
        isOnline: false,
        message: "Failed to load auction data",
      });
    }
  };

  // --------------------------------------------------
  // SOCKET CONNECTION
  // --------------------------------------------------

  useEffect(() => {
    let active = true;

    const initConnection = async () => {
      try {
        // ----------------------------------------------
        // TOKEN
        // ----------------------------------------------

        const token =
          localStorage.getItem("token");

        // ----------------------------------------------
        // USER
        // ----------------------------------------------

        const user = await getUser();

        // ----------------------------------------------
        // GUEST ID
        // ----------------------------------------------

        let guestId =
          localStorage.getItem("guest_id");

        if (!guestId) {
          guestId = `guest-${Math.random()
            .toString(36)
            .substring(2, 10)}`;

          localStorage.setItem(
            "guest_id",
            guestId
          );
        }

        // ----------------------------------------------
        // CREATE SOCKET
        // ----------------------------------------------

        const socketInstance =
          hostname(token);

        if (!socketInstance) {
          console.error(
            "Socket instance was not created."
          );

          return;
        }

        socketRef.current =
          socketInstance;

        setSocket(socketInstance);

        // ----------------------------------------------
        // DETECT MOBILE / WEB
        // ----------------------------------------------

        const isMobile =
          window.Capacitor
            ?.isNativePlatform?.() === true ||
          window.Capacitor
            ?.getPlatform?.() === "android" ||
          window.Capacitor
            ?.getPlatform?.() === "ios";

        const platform = isMobile
          ? "mobile"
          : "web";

        // ----------------------------------------------
        // CONNECTED USER DATA
        // ----------------------------------------------

        const connectedUserData = {
          userId:
            user?.id || guestId,

          email:
            user?.email ||
            "guest@user.com",

          group:
            user?.group ||
            "BIDDER",

          platform,
        };

        // ----------------------------------------------
        // REGISTER CONNECTED USER
        // ----------------------------------------------

        const registerConnectedUser = () => {
          if (!active) return;

          console.log(
            "Connected as:",
            connectedUserData.email,
            connectedUserData.platform
          );

          /*
           * Server will add:
           *
           * IP
           * Country
           * City
           * Region
           * Device
           * Connected time
           *
           * We only send user information
           * and platform from frontend.
           */

          socketInstance.emit(
            "ConnectedUser",
            connectedUserData
          );

          // Load auction data
          fetchAuctionData(
            socketInstance
          );
        };

        // ----------------------------------------------
        // SOCKET RECONNECT
        // ----------------------------------------------

        const handleReconnect = () => {
          if (!active) return;

          console.log(
            "Socket reconnected. Registering user again..."
          );

          /*
           * Important:
           *
           * Every reconnect creates a new
           * Socket.IO connection/socket ID.
           *
           * Therefore we register the user
           * again so the server updates:
           *
           * socketId
           * IP
           * platform
           * connectedAt
           */

          socketInstance.emit(
            "ConnectedUser",
            connectedUserData
          );

          fetchAuctionData(
            socketInstance
          );
        };

        // ----------------------------------------------
        // CONNECT ERROR
        // ----------------------------------------------

        const handleConnectError = (
          error
        ) => {
          console.error(
            "Socket connection error:",
            error
          );

          if (!active) return;

          setNetworkStatus({
            isOnline: false,
            message:
              "Socket connection failed",
          });
        };

        // ----------------------------------------------
        // CONNECT
        // ----------------------------------------------

        socketInstance.on(
          "connect",
          registerConnectedUser
        );

        // ----------------------------------------------
        // RECONNECT
        // ----------------------------------------------

        socketInstance.on(
          "reconnect",
          handleReconnect
        );

        // ----------------------------------------------
        // CONNECT ERROR
        // ----------------------------------------------

        socketInstance.on(
          "connect_error",
          handleConnectError
        );

        // ----------------------------------------------
        // ALREADY CONNECTED
        // ----------------------------------------------

        /*
         * Depending on how hostname() creates
         * the socket, it may already be connected
         * before the "connect" listener is attached.
         *
         * This handles that situation.
         */

        if (socketInstance.connected) {
          registerConnectedUser();
        }
      } catch (error) {
        console.error(
          "Socket initialization error:",
          error
        );

        if (!active) return;

        setNetworkStatus({
          isOnline: false,
          message:
            "Unable to connect to server",
        });
      }
    };

    initConnection();

    // --------------------------------------------------
    // BROWSER NETWORK STATUS
    // --------------------------------------------------

    const handleStatus = () => {
      setNetworkStatus((prev) => ({
        ...prev,
        isOnline:
          navigator.onLine,
      }));
    };

    window.addEventListener(
      "online",
      handleStatus
    );

    window.addEventListener(
      "offline",
      handleStatus
    );

    // --------------------------------------------------
    // CLEANUP
    // --------------------------------------------------

    return () => {
      active = false;

      if (socketRef.current) {
        /*
         * Remove only the listeners registered
         * by this component.
         *
         * Then disconnect this socket.
         */

        socketRef.current.off(
          "connect"
        );

        socketRef.current.off(
          "reconnect"
        );

        socketRef.current.off(
          "connect_error"
        );

        socketRef.current.disconnect();

        socketRef.current = null;
      }

      setSocket(null);

      window.removeEventListener(
        "online",
        handleStatus
      );

      window.removeEventListener(
        "offline",
        handleStatus
      );
    };
  }, []);

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (
    <Page
      className={classes.root}
      title="Console"
    >
      <Container
        maxWidth={false}
        style={{
          paddingLeft: phoneMatch
            ? 16
            : 0,

          paddingRight: phoneMatch
            ? 16
            : 0,
        }}
      >
        <Box>
          {socket && liveData && (
            <View
              socketDeclarator={socket}
              LiveData={liveData}
            />
          )}
        </Box>
      </Container>

      {/* --------------------------------------------- */}
      {/* NETWORK ERROR / LOADING                       */}
      {/* --------------------------------------------- */}

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) =>
            theme.zIndex.drawer + 1,
        }}
        open={!networkStatus.isOnline}
      >
        <Box textAlign="center">
          <b
            style={{
              fontSize: 20,
            }}
          >
            {networkStatus.message ||
              "Connection Lost..."}
          </b>

          <br />

          <CircularProgress
            color="inherit"
            sx={{
              mt: 2,
            }}
          />
        </Box>
      </Backdrop>
    </Page>
  );
};

export default SettingsPlayers;